import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  Fade,
  Card,
  CardContent,
  Paper,
  Divider,
  IconButton,
  Button,
} from "@mui/material";
import { Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";

import { UseHandleKanban } from "0.Mine/1.Hooks/CRM/Kanban";
import { UseHandleLeads } from "0.Mine/1.Hooks/CRM/Leads";
import { useHandleWorkFlow } from "0.Mine/1.Hooks/CRM/workflow";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";

// Dropdown Select Component
const DropdownSelect: React.FC<{
  options: string[];
  value: string;
  onChange: (value: string) => void;
}> = ({ options, value, onChange }) => {
  return (
    <FormControl
      size="small"
      sx={{ minWidth: 160, mx: 1, backgroundColor: "white", borderRadius: 2 }}
    >
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        displayEmpty
        variant="outlined"
        sx={{ fontWeight: 500 }}
      >
        <MenuItem disabled value="">
          <em>Seleziona...</em>
        </MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            {opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

// RuleRow Component
const RuleRow: React.FC<{
  id: string;
  ruleType: "tag" | "kanban";
  initialValue?: { tag?: string; kanban?: string; workflow?: string };
  onRemove: () => void;
}> = ({ id, ruleType, initialValue, onRemove }) => {
  const [tag, setTag] = useState(initialValue?.tag || "");
  const [workflow, setWorkflow] = useState(initialValue?.workflow || "");
  const [kanban, setKanban] = useState(initialValue?.kanban || "");
  const [tags, setTags] = useState<string[]>([]);
  const { session } = useBusinessContext();
  const [kanbanOptions, setKanbanOptions] = useState<string[]>([]);
  const [workflowOptions, setWorkflowOptions] = useState<string[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const { getLeadStatusOptions } = UseHandleLeads();
  const { getKanban } = UseHandleKanban();
  const { getallWorkFlows, sendOrUpdateRuleKanban, sendOrUpdateRuleStatus } = useHandleWorkFlow();

  // Only send rule to API when user makes changes (not on initial load)
  useEffect(() => {
    if (!isInitialized) return;

    const sendRule = async () => {
      if (session && session.selectedBusiness) {
        try {
          if (ruleType === "tag" && tag && workflow) {
            console.log("Sending tag rule:", { tag, workflow });
            await sendOrUpdateRuleStatus(session.selectedBusiness.id, tag, workflow);
          } else if (ruleType === "kanban" && kanban && workflow) {
            console.log("Sending kanban rule:", { kanban, workflow });
            await sendOrUpdateRuleKanban(session.selectedBusiness.id, kanban, workflow);
          }
        } catch (error) {
          console.error("Error saving rule:", error);
        }
      }
    };

    sendRule();
  }, [id, ruleType, tag, workflow, kanban, isInitialized]);

  // Fetch options for dropdowns
  useEffect(() => {
    const fetchData = async () => {
      if (session && session.selectedBusiness) {
        try {
          // Fetch tags
          const tagResponse = await getLeadStatusOptions(session.selectedBusiness.id);
          if (tagResponse && Array.isArray(tagResponse)) {
            setTags(tagResponse.map((tag) => tag.label));
          }

          // Fetch kanban options
          const kanbanResponse = await getKanban(session.selectedBusiness.id);
          if (kanbanResponse && kanbanResponse.columns && Array.isArray(kanbanResponse.columns)) {
            setKanbanOptions(kanbanResponse.columns.map((column) => column.name));
          }

          // Fetch workflow options
          const workflowResponse = await getallWorkFlows(session.selectedBusiness.id);
          if (workflowResponse && Array.isArray(workflowResponse)) {
            setWorkflowOptions(workflowResponse.map((workflow) => workflow.template_name));
          } else if (workflowResponse && typeof workflowResponse === "object") {
            // Try to find the array in the response object
            const possibleArray = Object.values(workflowResponse).find(Array.isArray);
            if (possibleArray && Array.isArray(possibleArray)) {
              setWorkflowOptions(possibleArray.map((workflow) => workflow.template_name));
            }
          }

          // Mark component as initialized after options are loaded
          setIsInitialized(true);
        } catch (error) {
          console.error("Error fetching options:", error);
        }
      }
    };

    fetchData();
  }, [session]);

  // Handlers for dropdown changes
  const handleTagChange = (value: string) => {
    setTag(value);
  };

  const handleWorkflowChange = (value: string) => {
    setWorkflow(value);
  };

  const handleKanbanChange = (value: string) => {
    setKanban(value);
  };

  return (
    <motion.div
      key={id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.4 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          paddingTop: "7px",
          paddingBottom: "7px",
          backgroundColor: "white",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="body2" sx={{ minWidth: 100, color: "#5a6eb4", fontSize: "0.875rem" }}>
          {ruleType === "tag" ? "Voglio che i lead con tag" : "Voglio che i lead nella colonna"}
        </Typography>

        <Box sx={{ minWidth: 160, mx: 2 }}>
          {ruleType === "tag" ? (
            <DropdownSelect options={tags} value={tag} onChange={handleTagChange} />
          ) : (
            <DropdownSelect options={kanbanOptions} value={kanban} onChange={handleKanbanChange} />
          )}
        </Box>

        <Typography variant="body2" sx={{ mr: 2, color: "#5a6eb4", fontSize: "0.875rem" }}>
          attivino il workflow
        </Typography>

        <Box sx={{ minWidth: 200 }}>
          <DropdownSelect
            options={workflowOptions}
            value={workflow}
            onChange={handleWorkflowChange}
          />
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <IconButton onClick={onRemove} color="error" sx={{ ml: 2 }}>
          <DeleteIcon />
        </IconButton>
      </Box>
    </motion.div>
  );
};

// Main RuleBuilder Component
const RuleBuilder: React.FC = () => {
  const { session } = useBusinessContext();
  const { getAllRules, deleteRule } = useHandleWorkFlow();
  const [rules, setRules] = useState<{ id: string; type: "tag" | "kanban"; initialValue?: any }[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);

  // Fetch rules from database on component mount
  useEffect(() => {
    const fetchData = async () => {
      if (session && session.selectedBusiness) {
        try {
          setIsLoading(true);
          const response = await getAllRules(session.selectedBusiness.id);

          if (!response) {
            console.error("No response from getAllRules");
            setIsLoading(false);
            return;
          }

          // Create a new array with all rules properly tagged
          const fetchedRules = [
            // Map kanban rules with their type
            ...(response.kanban?.map((rule) => ({
              id: rule.id.toString(),
              type: "kanban" as const,
              initialValue: {
                kanban: rule.rule_condition || "",
                workflow: rule.workflow_name || "",
              },
            })) || []),

            // Map status (tag) rules with their type
            ...(response.status?.map((rule) => ({
              id: rule.id.toString(),
              type: "tag" as const,
              initialValue: {
                tag: rule.rule_condition || "",
                workflow: rule.workflow_name || "",
              },
            })) || []),
          ];

          console.log("Fetched rules:", fetchedRules);
          setRules(fetchedRules);
        } catch (error) {
          console.error("Error fetching rules:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [session]);

  const addRule = (type: "tag" | "kanban") => {
    setRules([...rules, { id: crypto.randomUUID(), type }]);
  };

  const removeRule = async (id: string) => {
    setRules(rules.filter((r) => r.id !== id));
    //remove using api
    if (session && session.selectedBusiness) {
      try {
        await deleteRule(session.selectedBusiness.id, parseInt(id));
      } catch (error) {
        console.error("Error deleting rule:", error);
      }
    }
  };

  if (!session || !session.selectedBusiness) {
    return <p>Seleziona un business</p>;
  }
  return (
    <Box
      sx={{
        backgroundColor: "#ffffff",
        minHeight: "20vh",
        marginTop: "50px",
        borderRadius: "1rem",
        boxShadow: "0rem 0rem 1rem 0rem rgba(103, 116, 142, 0.075)",
      }}
    >
      <Box>
        {/* Header stile SequenceInfo */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 2,
            position: "relative",
            zIndex: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {/* Titolo + Tooltip */}
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ color: "#37376D" }}>
              Generatore di regole per la gestione leads
            </Typography>
            <Tooltip
              title={
                <>
                  Questa funzione permette di creare regole per le tag e le colonne nella sezione{" "}
                  <b>Gestione Leads</b>.<br />
                  Le regole per le tag sono disponibili solo per i lead che hanno una tag assegnata.
                  <br />
                  Le regole per le colonne leads sono disponibili solo per i lead che hanno una
                  colonna assegnata.
                </>
              }
              arrow
              placement="right"
            >
              <HelpOutlineIcon sx={{ fontSize: 20, color: "#37376D", cursor: "pointer" }} />
            </Tooltip>
          </Box>

          {/* Bottoni */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => addRule("tag")}
              size="small"
            >
              Regola tag lead
            </Button>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={() => addRule("kanban")}
              size="small"
            >
              Regola tipologia lead
            </Button>
          </Box>
        </Box>

        {/* Lista regole */}
        <Fade in timeout={600}>
          <Box>
            {isLoading ? (
              <Typography align="center">Caricamento regole...</Typography>
            ) : rules.length === 0 ? (
              <Typography align="center" sx={{ mt: 4, fontSize: "16px", color: "text.secondary" }}>
                Crea la tua prima regola per iniziare!
              </Typography>
            ) : (
              rules.map((rule) => (
                <RuleRow
                  key={rule.id}
                  id={rule.id}
                  ruleType={rule.type}
                  initialValue={rule.initialValue}
                  onRemove={() => removeRule(rule.id)}
                />
              ))
            )}
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};

export default RuleBuilder;
